import { StyleState } from "@kuika/kuika-cl-model";

/**
 * What the styling panel is editing right now, published for writers OUTSIDE the panel (the canvas
 * selection bar's quick style controls and style-tag chip).
 *
 * While the panel is in a state mode (hover / pressed / …) the component's base styleObj is a PREVIEW
 * mirror of that state, restored from the panel's backup when the state is left; in tag-edit mode the
 * component carries an unsaved tag edit that is discarded or saved as a whole; in condition mode the
 * panel's form holds a conditional style, which a re-read of the base styleObj would overwrite. A write from outside in
 * either mode is lost when the panel restores or commits its own copy, so those writers stay disabled
 * until the panel is back on the base style. The panel owns the value (`styling-window.tsx` publishes it
 * after every change and resets it when it unmounts); the flag lives outside the sd context so that
 * publishing it re-renders only its subscribers.
 */
export interface IStylingPanelMode {
  /** The component the panel is showing; null while no panel is mounted. */
  kuikaId: string | null;
  selectedState: StyleState;
  isEditMode: boolean;
  /** The panel's form is bound to one of the component's conditional styles, not its base style. */
  isConditionMode: boolean;
}

type Listener = () => void;

export const IDLE_STYLING_PANEL_MODE: IStylingPanelMode = {
  kuikaId: null,
  selectedState: "none",
  isEditMode: false,
  isConditionMode: false
};

export class StylingPanelModeStore {
  private snapshot: IStylingPanelMode = IDLE_STYLING_PANEL_MODE;

  private listeners: Set<Listener> = new Set<Listener>();

  getSnapshot = (): IStylingPanelMode => this.snapshot;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return (): void => {
      this.listeners.delete(listener);
    };
  };

  /** Stores the panel's mode; a value equal to the current one notifies nobody. */
  publish = (mode: IStylingPanelMode): void => {
    const current: IStylingPanelMode = this.snapshot;
    if (
      current.kuikaId === mode.kuikaId &&
      current.selectedState === mode.selectedState &&
      current.isEditMode === mode.isEditMode &&
      current.isConditionMode === mode.isConditionMode
    ) {
      return;
    }
    this.snapshot = { ...mode };
    this.listeners.forEach((listener: Listener): void => listener());
  };

  reset = (): void => this.publish(IDLE_STYLING_PANEL_MODE);

  /** The mounted panel's `flushPendingSubmit`; null while no panel is mounted. */
  private pendingEditFlusher: (() => void) | null = null;

  /** Registered by the panel on mount. */
  setPendingEditFlusher = (flusher: () => void): void => {
    this.pendingEditFlusher = flusher;
  };

  /** Unregistered by the panel on unmount — only its own flusher, never a newer panel's. */
  clearPendingEditFlusher = (flusher: () => void): void => {
    if (this.pendingEditFlusher === flusher) {
      this.pendingEditFlusher = null;
    }
  };

  /**
   * Commits the panel's pending (debounced) edit NOW, synchronously. An outside writer calls this before it
   * reads the styleObj it patches: the panel's edit then lands first and the outside write builds on it,
   * instead of the panel's later submit — which carries every form field, the outside-written one at its
   * old value — overwriting the outside write, or the outside write's reload dropping the panel's edit.
   */
  flushPendingEdit = (): void => {
    this.pendingEditFlusher?.();
  };
}

export const stylingPanelModeStore: StylingPanelModeStore = new StylingPanelModeStore();

const STATE_LABELS: Record<StyleState, string> = {
  none: "None",
  hover: "Hover",
  pressed: "Pressed",
  focused: "Focused",
  disabled: "Disabled"
};

/**
 * Why an outside style write on `kuikaId` must wait, or null when it can go ahead: the panel is showing
 * that component in a state mode, while editing a style tag, or with a conditional style selected.
 */
export function getOutsideStyleWriteLock(mode: IStylingPanelMode, kuikaId: string): string | null {
  if (mode.kuikaId !== kuikaId) {
    return null;
  }
  if (mode.isEditMode) {
    return "Finish editing the style tag in Styling to edit here";
  }
  if (mode.isConditionMode) {
    return "Return to the base style in Styling to edit here";
  }
  if (mode.selectedState !== "none") {
    return `Leave ${STATE_LABELS[mode.selectedState]} state in Styling to edit here`;
  }
  return null;
}
